import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  CalculatorIcon,
  CheckCircleIcon,
  ChartBarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { aporApi, hmdaApi } from '../lib/api';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';

const LOAN_TYPE_OPTIONS: Array<{ value: 'FIXED' | 'VARIABLE'; label: string }> = [
  { value: 'FIXED', label: 'Fixed Rate' },
  { value: 'VARIABLE', label: 'Variable Rate' },
];

export default function AporRateSpreadPage() {
  const [selectedValidationId, setSelectedValidationId] = useState<string>('');

  // Calculator state
  const [interestRate, setInterestRate] = useState('');
  const [actionDate, setActionDate] = useState('');
  const [loanTerm, setLoanTerm] = useState('');
  const [loanType, setLoanType] = useState<'FIXED' | 'VARIABLE'>('FIXED');
  const [calcResult, setCalcResult] = useState<any>(null);
  const [calcLoading, setCalcLoading] = useState(false);

  // Fetch validations for the selector
  const { data: validationsData } = useQuery({
    queryKey: ['hmda-validations'],
    queryFn: () => hmdaApi.getValidations({ limit: 50 }),
  });
  const validations = validationsData?.data?.validations || [];

  // APOR rates info
  const { data: ratesData } = useQuery({
    queryKey: ['apor-rates'],
    queryFn: () => aporApi.getRates(),
  });
  const ratesInfo = ratesData?.data;

  // Validation results
  const { data: validationResultsData, isLoading: validationLoading } = useQuery({
    queryKey: ['apor-validate', selectedValidationId],
    queryFn: () => aporApi.validate(selectedValidationId),
    enabled: !!selectedValidationId,
  });
  const validationResults = validationResultsData?.data?.results || [];
  const validationSummary = validationResultsData?.data?.summary;

  const handleCalculate = async () => {
    if (!interestRate || !actionDate || !loanTerm) {
      toast.error('Please fill in Interest Rate, Action Date, and Loan Term');
      return;
    }
    setCalcLoading(true);
    try {
      // NOTE: backend expects `repaymentTerm` (not loanTerm) and loanType in
      // FIXED/VARIABLE form. Frontend was sending loanTerm/FixedRate, which
      // 400'd silently and left the result panel empty.
      const res = await aporApi.calculate({
        interestRate: parseFloat(interestRate),
        actionDate,
        repaymentTerm: parseInt(loanTerm, 10),
        loanType,
      });
      if (res.data?.calculated === false) {
        toast.error(res.data.message || 'No APOR rate found for these inputs');
        setCalcResult(null);
      } else {
        setCalcResult(res.data);
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Calculation failed');
      setCalcResult(null);
    } finally {
      setCalcLoading(false);
    }
  };

  return (
    <FeatureGate feature="apor_rate_spread" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-gray-900">APOR Rate Spread</h1>
          <p className="mt-1 text-sm text-gray-500">
            Calculate and validate APOR rate spreads for HMDA reporting
          </p>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-blue-500" />
              <p className="text-xs font-medium text-gray-500">APOR Rates Loaded</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">{ratesInfo?.totalRates ?? 0}</p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <CheckCircleIcon className="h-5 w-5 text-green-500" />
              <p className="text-xs font-medium text-gray-500">Loans Validated</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {validationSummary?.totalLoans ?? 0}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <ArrowTrendingUpIcon className={`h-5 w-5 ${(validationSummary?.reportableSpreads ?? 0) > 0 ? 'text-orange-500' : 'text-gray-400'}`} />
              <p className="text-xs font-medium text-gray-500">Reportable Spreads</p>
            </div>
            <p className={`mt-1 text-2xl font-bold ${(validationSummary?.reportableSpreads ?? 0) > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
              {validationSummary?.reportableSpreads ?? 0}
            </p>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-2">
              <CalculatorIcon className="h-5 w-5 text-purple-500" />
              <p className="text-xs font-medium text-gray-500">Avg Rate Spread</p>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900">
              {validationSummary?.avgRateSpread != null
                ? `${validationSummary.avgRateSpread.toFixed(2)}%`
                : '--'}
            </p>
          </div>
        </div>

        {/* Calculator */}
        <div className="rounded-lg border bg-white p-4">
          <h3 className="text-sm font-semibold text-gray-900">Rate Spread Calculator</h3>
          <p className="mt-0.5 text-xs text-gray-400">
            Calculate the rate spread between a loan interest rate and the applicable APOR
          </p>
          <div className="mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <label className="block text-xs font-medium text-gray-700">Interest Rate (%)</label>
              <input
                type="number"
                step="0.001"
                value={interestRate}
                onChange={(e) => setInterestRate(e.target.value)}
                placeholder="e.g., 6.5"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">Action Date</label>
              <input
                type="date"
                value={actionDate}
                onChange={(e) => setActionDate(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">Repayment Term (years)</label>
              <input
                type="number"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
                placeholder="e.g., 30"
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700">Loan Type</label>
              <select
                value={loanType}
                onChange={(e) => setLoanType(e.target.value as 'FIXED' | 'VARIABLE')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-teal-500 focus:outline-none focus:ring-teal-500"
              >
                {LOAN_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="mt-4">
            <button
              onClick={handleCalculate}
              disabled={calcLoading}
              className="inline-flex items-center rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
            >
              <CalculatorIcon className="mr-2 h-4 w-4" />
              {calcLoading ? 'Calculating...' : 'Calculate'}
            </button>
          </div>
          {calcResult && (
            <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-4">
                <div>
                  <dt className="text-xs font-medium text-gray-500">APOR Rate</dt>
                  <dd className="mt-0.5 text-lg font-bold text-gray-900">
                    {calcResult.aporRate != null ? `${calcResult.aporRate.toFixed(3)}%` : '--'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Rate Spread</dt>
                  <dd className="mt-0.5 text-lg font-bold text-gray-900">
                    {calcResult.rateSpread != null ? `${calcResult.rateSpread.toFixed(3)}%` : '--'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">Reportable</dt>
                  <dd className="mt-0.5">
                    <span
                      className={clsx(
                        'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                        calcResult.isReportable
                          ? 'bg-red-100 text-red-700'
                          : 'bg-green-100 text-green-700'
                      )}
                    >
                      {calcResult.isReportable ? 'Yes - HOEPA/HPML' : 'No'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs font-medium text-gray-500">APOR Effective Date</dt>
                  <dd className="mt-0.5 text-sm text-gray-900">
                    {calcResult.aporEffectiveDate
                      ? new Date(calcResult.aporEffectiveDate).toLocaleDateString()
                      : actionDate}
                  </dd>
                </div>
              </dl>
            </div>
          )}
        </div>

        {/* Validation Results */}
        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Validation Results</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  Select a validation to check APOR rate spreads across all loans
                </p>
              </div>
              <select
                value={selectedValidationId}
                onChange={(e) => setSelectedValidationId(e.target.value)}
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-blue-500"
              >
                <option value="">Select a validation...</option>
                {validations.map((v: any) => (
                  <option key={v.id} value={v.id}>
                    {v.fileName || v.id} - {new Date(v.createdAt).toLocaleDateString()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {!selectedValidationId ? (
            <div className="py-12 text-center text-gray-500">
              <CalculatorIcon className="mx-auto h-12 w-12 text-gray-300" />
              <p className="mt-4 text-lg font-medium">No validation selected</p>
              <p className="mt-1 text-sm">Choose a validation to see rate spread results.</p>
            </div>
          ) : validationLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
            </div>
          ) : validationResults.length === 0 ? (
            <div className="py-12 text-center text-gray-500">
              <p className="text-sm">No rate spread results for this validation.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Row #</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Interest Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">APOR Rate</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Rate Spread</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Reportable</th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Match</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {validationResults.map((r: any, i: number) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                        {r.rowNumber || i + 1}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900">
                        {r.interestRate != null ? `${r.interestRate}%` : '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-600">
                        {r.aporRate != null ? `${r.aporRate.toFixed(3)}%` : '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-gray-900">
                        {r.rateSpread != null ? `${r.rateSpread.toFixed(3)}%` : '--'}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={clsx(
                            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                            r.reportable
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-600'
                          )}
                        >
                          {r.reportable ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3">
                        <span
                          className={clsx(
                            'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                            r.match
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          )}
                        >
                          {r.match ? 'Match' : 'Mismatch'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </FeatureGate>
  );
}
